import { createFileRoute, redirect } from "@tanstack/react-router";
import DownloadSalesBrochureDialog from "lib/components/showroom2/components/DownloadSalesBrochureDialog";
import { useShowroomPageDataSetup } from "lib/components/showroom2/data/useShowroomPageDataSetup";
import {
  useIsPublicBrochureDownloadHiddenForMultiUnit,
  useIsPublicBrochureDownloadEnabled,
} from "lib/components/showroom2/data/useShowroomPlanData";
import ShowroomPage from "lib/components/showroom2/layout/ShowroomPage";
import useShowroomEvents from "lib/components/showroom2/state/useShowroomEvents";
import { ShowroomProvider } from "lib/components/showroom2/state/useShowroomState";
import { errorPath, useAppRouterParams } from "lib/routeHelpers";
import { useState } from "react";
import Button from "ui/buttons/Button";
import IconDownload from "ui/icons/IconDownload";
import styles from "./ShowroomPlanOverview.module.scss";

/**
 * Public post-CTA confirmation page for a saved `HomePlan`. Shows a "your
 * plan has been saved" message and surfaces a "Download Sales Brochure"
 * button that opens the public T&C modal.
 */
function ShowroomPlanOverview() {
  const { homePlanId, builderId } = useAppRouterParams();
  const showroomKey = [builderId, homePlanId].filter(Boolean).join("-") || undefined;

  return (
    <ShowroomProvider key={showroomKey} version="sales">
      <ShowroomPlanOverviewContent />
    </ShowroomProvider>
  );
}

function ShowroomPlanOverviewContent() {
  const { planData, planDataLoading } = useShowroomPageDataSetup();
  const brochureDownloadEnabled = useIsPublicBrochureDownloadEnabled();
  const hideBrochureForMultiUnit = useIsPublicBrochureDownloadHiddenForMultiUnit();
  const sendShowroomEvent = useShowroomEvents();
  const [downloadOpen, setDownloadOpen] = useState(false);
  const showBrochureDownload = brochureDownloadEnabled && !hideBrochureForMultiUnit;
  const savedMessage = `Your plan has been saved. Review your configuration below${
    showBrochureDownload ? " or download a sales brochure" : ""
  }.`;

  const openDownloadDialog = (): void => {
    setDownloadOpen(true);
    sendShowroomEvent("showroom-intro:clicked brochure download button");
  };

  return (
    <ShowroomPage pageTitle="Your plan is saved" loading={planDataLoading}>
      <div className={styles.wrap}>
        <header className={styles.header}>
          <h1 className={styles.title}>{planData?.homePlan?.planName ?? "Your plan"}</h1>
          {showBrochureDownload ? (
            <div className={styles.actions}>
              <Button
                btnStyle="primary"
                btnSize="large"
                onClick={openDownloadDialog}
                disabled={!planData}
                data-testid="overview-download-brochure-button"
              >
                <IconDownload size={14} />
                <span>Sales Brochure</span>
              </Button>
            </div>
          ) : null}
        </header>

        <div className={styles.successBanner} role="status" aria-live="polite">
          <span className={styles.checkmark} aria-hidden="true">
            ✓
          </span>
          <span>{savedMessage}</span>
        </div>
      </div>
      {downloadOpen && <DownloadSalesBrochureDialog open={downloadOpen} onClose={() => setDownloadOpen(false)} />}
    </ShowroomPage>
  );
}

export const Route = createFileRoute("/showroom/plan/$home_plan_id/overview")({
  // Gate at the route level. See `apps/web/pages/showroom/index.tsx` for the
  // hydration-deferral rationale.
  beforeLoad: ({ context }) => {
    if (!context.flagsHydrated) {
      return;
    }
    if (!context.enabledFeatures.showroomUnification) {
      throw redirect({
        to: errorPath(403, undefined, "This feature is not enabled for your account."),
        replace: true,
      });
    }
    if (!context.enabledProducts.showroom) {
      throw redirect({
        to: errorPath(403, undefined, "The product you are trying to access is not enabled for this organization."),
        replace: true,
      });
    }
  },
  component: ShowroomPlanOverview,
  staticData: { isPublicPage: true },
});
